import React, { useEffect } from 'react';
import { AlertTriangle, X, RefreshCw, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import { useCheckPaymentStatusQuery } from '../../store/services/api';

interface PaymentFailedProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId?: string;
  error?: string;
}

const PaymentFailed = ({ isOpen, onClose, sessionId, error: propError }: PaymentFailedProps) => {
  // Ödeme durumunu kontrol et
  const { data: paymentStatus, isLoading } = useCheckPaymentStatusQuery(
    sessionId || '', 
    { skip: !sessionId || !isOpen }
  );

  // Component did mount - Listen for custom event
  useEffect(() => {
    const handleOpenEvent = () => {
      console.log('Received openBuyCredits event, will open modal');
    };
    
    document.addEventListener('openBuyCredits', handleOpenEvent);
    
    return () => {
      document.removeEventListener('openBuyCredits', handleOpenEvent);
    };
  }, []);

  // Eğer ödeme başarılı olursa modalı kapat
  useEffect(() => {
    if (paymentStatus?.status === 'completed') {
      onClose();
    }
  }, [paymentStatus?.status, onClose]);

  if (!isOpen) return null;

  const errorMessage = propError || paymentStatus?.error || "There was an issue processing your payment. Please try again later or use a different payment method.";

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-[2px]"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div 
        className="bg-white rounded-lg w-full max-w-md shadow-lg relative p-6" 
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        
        {isLoading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#FF4444]" />
            <p className="text-gray-600">Checking payment status...</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-5">
              <div className="bg-red-100 w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Payment Failed</h2>
              <p className="text-gray-600 mt-2">{errorMessage}</p>
            </div>
            
            <div className="space-y-3">
              <Button
                onClick={onClose}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
              
              <Button
                onClick={() => {
                  onClose();
                  // Trigger the BuyMoreCredits modal to open after a short delay
                  setTimeout(() => {
                    const buyCreditsEvent = new CustomEvent('openBuyCredits');
                    document.dispatchEvent(buyCreditsEvent);
                  }, 300);
                }}
                className="w-full flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentFailed; 